import BasePlugin from "@uppy/core/lib/BasePlugin.js";
import { extractDuration } from "./MediaDurationExtractor.js";
import criticalLoggingService from "./CriticalLoggingService.js";

/**
 * Production-grade Uppy uploader plugin for direct-to-GCS chunked resumable uploads.
 *
 * Features:
 * - 8 MiB chunked uploads with Content-Range headers
 * - Automatic retry with exponential backoff on transient failures
 * - Browser crash recovery via localStorage session persistence
 * - File fingerprinting for upload resume detection
 * - GCS offset probing to resume from last confirmed byte
 * - Two-tier logging: console (high-frequency) + CriticalLoggingService (lifecycle)
 *
 * Protocol: GCS resumable uploads (not TUS), direct browser-to-GCS (no proxy)
 */

/**
 * Namespaced so they cannot collide with Uppy Core's own events. Uppy owns
 * `upload-stalled` with the signature (error, files) and calls files.map() on
 * it — emitting a different shape under that name throws inside Uppy.
 */
export const STALLED_EVENT = "audioscribe:upload-stalled";
export const RESUMED_EVENT = "audioscribe:upload-resumed";

/** localStorage namespace for resumable-upload sessions. */
export const STORAGE_PREFIX = "audioscribe:gcs-upload:v1:";

/** A GCS resumable session is valid for 7 days; past that it cannot be resumed. */
export const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Unfinished uploads this browser still holds a resumable session for.
 *
 * Resume already works — the plugin probes GCS for the confirmed offset and
 * continues from there — but it only ever fires when the user happens to
 * re-add the exact same file, which almost nobody knows to do (49
 * `session.resume` events against 2,523 `upload.start` over 14 days). This is
 * the read side that lets the UI say so out loud.
 *
 * Deliberately a PURE READ: it never removes anything. Pruning stays the
 * plugin's job (`cleanupStaleSessions`) so that merely displaying the list can
 * never destroy a session the user is about to resume. It also never returns
 * `sessionUri` — that value is a bearer credential for the GCS upload and must
 * not reach the DOM.
 */
export function listUnfinishedUploads(now = Date.now()) {
  const found = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;

      try {
        const session = JSON.parse(localStorage.getItem(key));
        // A session with no filename can't be described to the user, and one
        // with no timestamp can't be aged out — skip both rather than guess.
        if (!session?.fileName || typeof session.createdAt !== "number")
          continue;
        if (now - session.createdAt > SESSION_MAX_AGE_MS) continue;

        // Only advertise what can actually be resumed. EVERY session needs its
        // audioFileUUID, because both resume paths end in
        // finalizeUpload(audioFileUUID, ...) — without it the finalize POSTs
        // `undefined` and can never complete. One still in flight additionally
        // needs the session URI to probe the offset. Naming a file the uploader
        // would then fail on sends the user on a dead errand.
        if (!session.audioFileUUID || typeof session.audioFileUUID !== "string")
          continue;
        const pendingFinalize = session.status === "uploaded_pending_finalize";
        if (!pendingFinalize && typeof session.sessionUri !== "string") continue;

        found.push({
          fingerprint: key.slice(STORAGE_PREFIX.length),
          fileName: session.fileName,
          totalSize: session.totalSize ?? 0,
          createdAt: session.createdAt,
          // Bytes are already in GCS and only the finalize handshake is left,
          // so re-adding the file completes it without re-uploading.
          pendingFinalize,
        });
      } catch {
        // Corrupt entry — not worth surfacing; cleanupStaleSessions removes it.
      }
    }
  } catch {
    // localStorage can be entirely unavailable (private mode, blocked storage).
  }

  return found.sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Raised when an upload attempt is cancelled — by the user removing the file,
 * by cancel-all, or by the plugin being torn down.
 *
 * A real Error rather than a thrown literal, so it carries a stack; `cancelled`
 * is what every check along the upload path keys on, and `transient` marks it
 * as something no retry should chase.
 */
export class UploadCancelledError extends Error {
  constructor(message = "Upload cancelled") {
    super(message);
    this.name = "UploadCancelledError";
    this.cancelled = true;
    this.transient = false;
  }
}

export default class UppyGcsUploader extends BasePlugin {
  constructor(uppy, opts) {
    super(uppy, opts);
    this.id = opts.id || "GcsUploader";
    this.type = "uploader";

    // Config
    this.apiBaseUrl = opts.apiBaseUrl;
    this.getAccessToken = opts.getAccessToken;
    this.workspaceUUID = opts.workspaceUUID;
    this.userUUID = opts.userUUID;
    this.language = opts.language;

    // Chunking config
    this.chunkSize = opts.chunkSize || 8 * 1024 * 1024; // 8 MiB default
    this.maxRetries = opts.maxRetries || 5;

    // Constants. Sourced from the module-level exports so the resume banner
    // reads exactly the keys this plugin writes, and the two can never drift.
    this.STORAGE_PREFIX = STORAGE_PREFIX;
    this.SESSION_MAX_AGE_MS = SESSION_MAX_AGE_MS;
    this.FINALIZE_MAX_RETRIES = 3;

    // Stall recovery. Exhausting the per-chunk retries does NOT mean the
    // upload is dead: the GCS session stays valid for days and the File is
    // still in memory, so the transfer can pick up where it stopped once
    // conditions improve. Backgrounding a tab (or sleeping the laptop) throttles
    // and then suspends it, which is exactly how a healthy upload used to die.
    this.MAX_STALL_CYCLES = opts.maxStallCycles ?? 20;
    this.STALL_POLL_MS = opts.stallPollMs ?? 15000;
    this.MAX_STALL_POLL_MS = opts.maxStallPollMs ?? 5 * 60 * 1000;

    /**
     * In-flight upload attempts. Cancellation is keyed to the ATTEMPT, not to
     * the file id: Uppy derives ids deterministically from the file itself, so
     * removing and re-adding the same file reuses its id, and any id-keyed
     * record cannot tell one incarnation from the next. A per-attempt token
     * also makes the state self-clearing — it lives exactly as long as the
     * transfer it belongs to.
     *
     * The token is a latch, not an edge: a cancel can land while the upload is
     * mid-probe or mid-retry, with nothing parked to wake, and that must still
     * be seen at the next check rather than lost.
     */
    this.activeUploads = new Set();

    this.handleUpload = this.handleUpload.bind(this);
    this.handleCancelAll = this.cancelAllUploads.bind(this);
    this.handleFileRemoved = (file) => this.cancelUpload(file?.id);
  }

  // ── Lifecycle ─────────────────────────────────────────────

  install() {
    this.uppy.addUploader(this.handleUpload);
    this.uppy.on("cancel-all", this.handleCancelAll);
    this.uppy.on("file-removed", this.handleFileRemoved);
    this.cleanupStaleSessions();
  }

  uninstall() {
    this.uppy.removeUploader(this.handleUpload);
    this.uppy.off("cancel-all", this.handleCancelAll);
    this.uppy.off("file-removed", this.handleFileRemoved);
    // Nothing may outlive the plugin — a parked upload would otherwise keep a
    // timer and DOM listeners alive after the component is gone, and latching
    // stops an in-flight transfer from arming a fresh wait after teardown.
    this.cancelAllUploads();
  }

  // ── Logging ───────────────────────────────────────────────

  /**
   * Two-tier logging:
   * - "console": browser DevTools only (high-frequency)
   * - "server": console + CriticalLoggingService (lifecycle/errors)
   */
  log(event, data = {}, tier = "console") {
    const timestamp = new Date().toISOString();

    // The session URI is a bearer credential for the GCS upload. Redact it ONCE
    // here so no call site can leak it: redaction used to live only on the
    // server payload below, so the console branch printed the full token for
    // any caller that passed it raw (e.g. probe.no_range). Call sites that
    // already truncate are left alone rather than double-prefixed.
    const safeData =
      typeof data.sessionUri === "string" && !data.sessionUri.startsWith("...")
        ? { ...data, sessionUri: `...${data.sessionUri.slice(-20)}` }
        : data;

    const logEntry = { event, ...safeData, timestamp };

    // Always log to console
    const prefix = `[GcsUploader] [${event}]`;
    if (data.error) {
      console.error(prefix, logEntry);
    } else {
      console.log(prefix, logEntry);
    }

    // Server-tier: also persist via CriticalLoggingService
    if (tier === "server") {
      criticalLoggingService.logCriticalEvent({
        level: data.error ? "error" : "info",
        category: "GCS_UPLOAD",
        message: `GcsUploader: ${event}`,
        // Already redacted above, for every tier.
        data: safeData,
        audioFileId: data.audioFileUUID || data.uploadId,
      }).catch(() => {}); // Non-blocking
    }
  }

  // ── Fingerprinting ────────────────────────────────────────

  /**
   * Generate a fingerprint for a file to match it to an existing upload session.
   * Uses name + size + lastModified + type, similar to TUS protocol.
   */
  generateFingerprint(file) {
    const name = file.name || "unknown";
    const size = file.data?.size || file.size || 0;
    const type = file.type || "";

    // lastModified can return current time if unknown — detect and fall back
    let lastModified = file.data?.lastModified || 0;
    const now = Date.now();
    if (Math.abs(lastModified - now) < 5000) {
      // Looks synthetic (within 5s of now) — don't use it
      lastModified = 0;
    }

    return `${name}-${size}-${lastModified}-${type}`;
  }

  // ── Session Persistence (localStorage) ────────────────────

  getStoredSession(fingerprint) {
    try {
      const key = this.STORAGE_PREFIX + fingerprint;
      const data = localStorage.getItem(key);
      if (!data) return null;

      const session = JSON.parse(data);

      // Check if expired
      if (Date.now() - session.createdAt > this.SESSION_MAX_AGE_MS) {
        this.log("session.expired", {
          fingerprint,
          sessionAge: Date.now() - session.createdAt,
        }, "server");
        localStorage.removeItem(key);
        return null;
      }

      return session;
    } catch {
      return null;
    }
  }

  storeSession(fingerprint, data) {
    try {
      const key = this.STORAGE_PREFIX + fingerprint;
      localStorage.setItem(key, JSON.stringify(data));
    } catch (err) {
      this.log("session.store_failed", { fingerprint, error: err.message }, "server");
    }
  }

  updateSessionState(fingerprint, updates) {
    const session = this.getStoredSession(fingerprint);
    if (session) {
      this.storeSession(fingerprint, { ...session, ...updates });
    }
  }

  clearSession(fingerprint) {
    try {
      localStorage.removeItem(this.STORAGE_PREFIX + fingerprint);
    } catch {
      // Ignore
    }
  }

  cleanupStaleSessions() {
    let cleaned = 0;
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key?.startsWith(this.STORAGE_PREFIX)) continue;

        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (Date.now() - data.createdAt > this.SESSION_MAX_AGE_MS) {
            localStorage.removeItem(key);
            cleaned++;
          }
        } catch {
          localStorage.removeItem(key);
          cleaned++;
        }
      }
    } catch {
      // Ignore
    }

    if (cleaned > 0) {
      this.log("cleanup.stale", { count: cleaned });
    }
  }

  // ── GCS Operations ────────────────────────────────────────

  /**
   * Probe GCS for the current upload offset.
   * Sends PUT with Content-Range: bytes * /{total} and empty body.
   * GCS responds with 308 + Range header, or 200 if complete.
   */
  async probeUploadOffset(sessionUri, totalSize) {
    this.log("probe.start", { sessionUri: `...${sessionUri.slice(-20)}`, totalSize });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", sessionUri, true);
      xhr.setRequestHeader("Content-Range", `bytes */${totalSize}`);
      xhr.setRequestHeader("Content-Length", "0");

      xhr.onload = () => {
        if (xhr.status === 308) {
          const range = xhr.getResponseHeader("Range");
          let confirmedBytes = 0;

          if (range) {
            const match = range.match(/bytes=\d+-(\d+)/);
            confirmedBytes = match ? Number.parseInt(match[1]) + 1 : 0;
          } else {
            this.log("probe.no_range", { sessionUri, totalSize }, "server");
          }

          this.log("probe.result", { confirmedBytes, totalSize }, "server");
          resolve(confirmedBytes);
        } else if (xhr.status === 200 || xhr.status === 201) {
          // Upload already complete
          this.log("probe.result", { confirmedBytes: totalSize, totalSize, complete: true }, "server");
          resolve(totalSize);
        } else if (xhr.status === 404 || xhr.status === 410) {
          reject({ expired: true, status: xhr.status });
        } else {
          reject({ transient: true, status: xhr.status });
        }
      };

      xhr.onerror = () => reject({ transient: true, status: 0, message: "network error" });
      xhr.timeout = 30000;
      xhr.ontimeout = () => reject({ transient: true, status: 0, message: "timeout" });

      xhr.send(new Blob([])); // Empty body
    });
  }

  /**
   * Upload a single chunk to GCS with Content-Range header.
   * Returns { status: "continue"|"complete", confirmedBytes }
   */
  uploadChunk(sessionUri, fileBlob, start, end, total, file) {
    const chunk = fileBlob.slice(start, end);
    const chunkIndex = Math.floor(start / this.chunkSize);

    this.log("chunk.start", {
      chunkIndex, startByte: start, endByte: end,
      chunkSize: end - start, totalSize: total,
    });

    // Adaptive timeout: at least 2 min, or based on chunk size at 50KB/s minimum
    const timeout = Math.max(120000, ((end - start) / 50000) * 1000);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", sessionUri, true);
      xhr.setRequestHeader("Content-Range", `bytes ${start}-${end - 1}/${total}`);

      // Intra-chunk progress (console-only, high frequency)
      xhr.upload.onprogress = (e) => {
        const overallBytes = start + e.loaded;
        const percent = ((overallBytes / total) * 100).toFixed(1);
        this.log("chunk.progress", { chunkIndex, bytesUploaded: e.loaded, overallPercent: percent });

        // Emit to Uppy for Dashboard progress bar
        this.uppy.emit("upload-progress", file, {
          uploader: this,
          bytesUploaded: overallBytes,
          bytesTotal: total,
        });
      };

      xhr.onload = () => {
        if (xhr.status === 308) {
          const range = xhr.getResponseHeader("Range");
          let confirmed = 0;
          if (range) {
            const match = range.match(/bytes=\d+-(\d+)/);
            confirmed = match ? Number.parseInt(match[1]) + 1 : 0;
          }
          this.log("chunk.ack", { chunkIndex, confirmedBytes: confirmed });
          resolve({ status: "continue", confirmedBytes: confirmed });
        } else if (xhr.status === 200 || xhr.status === 201) {
          this.log("chunk.ack", { chunkIndex, confirmedBytes: total, final: true });
          resolve({ status: "complete", confirmedBytes: total });
        } else if (xhr.status === 404 || xhr.status === 410) {
          reject({ transient: false, expired: true, status: xhr.status });
        } else if (xhr.status >= 500 || xhr.status === 429 || xhr.status === 408) {
          // Transient — retryable (408 Request Timeout is retryable per GCS spec)
          reject({ transient: true, status: xhr.status });
        } else {
          reject({ transient: false, status: xhr.status, body: xhr.responseText });
        }
      };

      xhr.onerror = () => reject({ transient: true, status: 0, message: "network error" });
      xhr.ontimeout = () => reject({ transient: true, status: 0, message: "timeout" });
      xhr.timeout = timeout;

      xhr.send(chunk);
    });
  }

  // ── Retry Logic ───────────────────────────────────────────

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Park a stalled upload until something suggests it can make progress again.
   *
   * Resolves as soon as the tab is foregrounded or the browser reports the
   * network is back, and otherwise after a poll interval — so recovery never
   * depends on an event actually firing. The interval escalates, because the
   * common case is a laptop that will be asleep for a while, and hammering a
   * dead connection every 15 seconds helps nobody.
   */
  waitForResumeSignal(stallCycle = 1, token = null) {
    const delay = Math.min(
      this.STALL_POLL_MS * Math.pow(2, stallCycle - 1),
      this.MAX_STALL_POLL_MS
    );

    return new Promise((resolve) => {
      let settled = false;
      let pollId;

      const cleanup = () => {
        clearTimeout(pollId);
        if (token) token.wake = null;
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
        if (typeof window !== "undefined") {
          window.removeEventListener("online", onOnline);
        }
      };

      const finish = (reason) => {
        if (settled) return;
        settled = true;
        cleanup();
        this.log("upload.resume_signal", { reason, stallCycle, delay });
        resolve(reason);
      };

      const onVisibilityChange = () => {
        if (
          typeof document === "undefined" ||
          document.visibilityState === "visible"
        ) {
          finish("visible");
        }
      };
      const onOnline = () => finish("online");

      pollId = setTimeout(() => finish("poll"), delay);

      // A parked upload must be interruptible. Without this, cancelling a file
      // only removes it from the UI: the timer and listeners stay armed, and a
      // later poll or `online` event would resume — and finalize — an upload
      // the user already cancelled. Check the latch too: a cancel may already
      // have landed before we got here.
      if (token) {
        if (token.cancelled) return finish("aborted");
        token.wake = () => finish("aborted");
      }

      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibilityChange);
      }
      if (typeof window !== "undefined") {
        window.addEventListener("online", onOnline);
      }
    });
  }

  /**
   * Begin tracking one upload attempt. The returned token IS the cancellation
   * latch for that attempt and nothing else.
   */
  beginUpload(fileID) {
    const token = { fileID, cancelled: false, wake: null };
    this.activeUploads.add(token);
    return token;
  }

  /** Stop tracking an attempt once its transfer has ended, however it ended. */
  endUpload(token) {
    this.activeUploads.delete(token);
  }

  /** Latch one attempt as cancelled and wake it if it is parked. */
  cancelToken(token) {
    token.cancelled = true;
    token.wake?.();
  }

  /** Cancel every in-flight attempt for a file. */
  cancelUpload(fileID) {
    if (!fileID) return;
    for (const token of [...this.activeUploads]) {
      if (token.fileID === fileID) this.cancelToken(token);
    }
  }

  /** Cancel every in-flight attempt, whatever the file. */
  cancelAllUploads() {
    for (const token of [...this.activeUploads]) this.cancelToken(token);
  }

  calculateBackoff(attempt) {
    const base = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s, 8s, 16s
    const jitter = Math.random() * 1000;
    return base + jitter;
  }

  async uploadChunkWithRetry(sessionUri, fileBlob, start, end, total, file, fingerprint, token) {
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const result = await this.uploadChunk(sessionUri, fileBlob, start, end, total, file);

        // Update localStorage with latest confirmed offset
        this.updateSessionState(fingerprint, { lastKnownOffset: result.confirmedBytes });

        return result;
      } catch (err) {
        if (err.expired) {
          // Session expired — caller must handle
          throw err;
        }

        if (!err.transient || attempt >= this.maxRetries) {
          this.log("chunk.failed", {
            chunkIndex: Math.floor(start / this.chunkSize),
            error: err.message || `HTTP ${err.status}`,
            attempts: attempt + 1,
          }, "server");
          throw err;
        }

        const delay = this.calculateBackoff(attempt);
        this.log("chunk.retry", {
          chunkIndex: Math.floor(start / this.chunkSize),
          attempt: attempt + 1,
          delay: Math.round(delay),
          error: err.message || `HTTP ${err.status}`,
        }, "server");

        await this.sleep(delay);

        // Nothing may resume across a cancel: the backoff sleep is the longest
        // window in which teardown can land unnoticed.
        if (token?.cancelled) throw new UploadCancelledError();

        // After retry delay, probe GCS for actual offset in case bytes were partially received
        try {
          const confirmedOffset = await this.probeUploadOffset(sessionUri, total);
          if (confirmedOffset >= end) {
            // This chunk was actually received — move on
            this.log("chunk.ack", {
              chunkIndex: Math.floor(start / this.chunkSize),
              confirmedBytes: confirmedOffset,
              recoveredViaProbe: true,
            });
            return { status: confirmedOffset >= total ? "complete" : "continue", confirmedBytes: confirmedOffset };
          }
          // Resume from where GCS actually is
          start = confirmedOffset;
        } catch (probeErr) {
          if (probeErr.expired) throw probeErr;
          // Probe failed — retry the chunk from original offset
        }
      }
    }
  }

  /**
   * Upload a file in chunks with resume support.
   */
  async uploadFileChunked(file, sessionUri, startOffset, fingerprint, token) {
    const fileBlob = file.data;
    const total = fileBlob.size;
    let offset = startOffset;
    const startTime = Date.now();
    let chunkCount = 0;

    this.log("upload.start", {
      fileName: file.name,
      fileSize: total,
      isResume: startOffset > 0,
      resumeOffset: startOffset,
      chunkSize: this.chunkSize,
      totalChunks: Math.ceil((total - startOffset) / this.chunkSize),
    }, "server");

    let stallCycles = 0;
    const cancelled = () => token?.cancelled === true;

    while (offset < total) {
      if (cancelled()) throw new UploadCancelledError();

      const end = Math.min(offset + this.chunkSize, total);

      let result;
      try {
        result = await this.uploadChunkWithRetry(
          sessionUri, fileBlob, offset, end, total, file, fingerprint, token
        );
        stallCycles = 0;
      } catch (err) {
        // A session GCS has forgotten, or a genuinely permanent error, still
        // fails fast — retrying those achieves nothing.
        if (err.expired || !err.transient) throw err;

        if (stallCycles >= this.MAX_STALL_CYCLES) {
          this.log("upload.stall_exhausted", {
            confirmedBytes: offset,
            totalSize: total,
            stallCycles,
          }, "server");
          throw err;
        }

        // Exhausting the per-chunk retries is NOT the end of the upload. The
        // page is alive, the File is in memory and the GCS session is good for
        // days — so park and pick up where GCS actually is once the tab comes
        // back or the network returns.
        stallCycles++;
        this.log("upload.stalled", {
          confirmedBytes: offset,
          totalSize: total,
          stallCycle: stallCycles,
          error: err.message || `HTTP ${err.status}`,
        }, "server");
        // NOTE: namespaced deliberately. Uppy Core owns `upload-stalled` with
        // the signature (error, files) and calls files.map() on it, so emitting
        // our own shape under that name throws inside Uppy and kills the very
        // upload this block exists to save.
        this.uppy.emit(STALLED_EVENT, file, {
          confirmedBytes: offset,
          totalSize: total,
        });

        // Check the latch BEFORE parking too: a cancel that arrived during the
        // chunk retries had no waiter to wake, so only the latch records it.
        if (cancelled()) throw new UploadCancelledError();

        const signal = await this.waitForResumeSignal(stallCycles, token);
        if (signal === "aborted" || cancelled()) {
          this.log("upload.stall_aborted", {
            fileName: file.name,
            confirmedBytes: offset,
          }, "server");
          throw new UploadCancelledError();
        }

        // Conditions may have changed while parked — ask GCS where it really is
        // rather than assuming our last known offset still holds.
        let probedOffset = null;
        try {
          probedOffset = await this.probeUploadOffset(sessionUri, total);
        } catch (probeErr) {
          if (probeErr.expired) throw probeErr;
          // Still unreachable — loop round and park again.
        }

        // Deliberately outside the try: the probe is a network round trip and
        // a cancel can land during it, with no waiter left to wake. Throwing
        // inside the try above would be swallowed by its own catch.
        if (cancelled()) throw new UploadCancelledError();

        if (probedOffset !== null) {
          offset = probedOffset;
          this.updateSessionState(fingerprint, { lastKnownOffset: offset });
          this.log("upload.resumed_after_stall", {
            confirmedBytes: offset,
            totalSize: total,
            stallCycle: stallCycles,
          }, "server");
          this.uppy.emit(RESUMED_EVENT, file, {
            confirmedBytes: offset,
            totalSize: total,
          });
        }
        continue;
      }

      chunkCount++;
      offset = result.confirmedBytes;

      // Emit confirmed progress
      const elapsed = (Date.now() - startTime) / 1000;
      const throughput = offset / elapsed;
      const remaining = ((total - offset) / throughput);
      this.log("upload.progress", {
        confirmedBytes: offset,
        totalSize: total,
        percent: ((offset / total) * 100).toFixed(1),
        elapsedSec: elapsed.toFixed(1),
        estimatedRemainingSec: remaining.toFixed(0),
        throughputMbps: ((throughput * 8) / 1000000).toFixed(2),
      });

      if (result.status === "complete") break;
    }

    // The loop can exit on a successful final chunk without revisiting its
    // top-of-loop check, so a cancel that landed while that chunk was in flight
    // would otherwise be reported as a clean completion.
    if (cancelled()) throw new UploadCancelledError();

    const elapsed = (Date.now() - startTime) / 1000;
    this.log("upload.complete", {
      totalSize: total,
      totalChunks: chunkCount,
      elapsedMs: Date.now() - startTime,
      throughputMbps: ((total / elapsed * 8) / 1000000).toFixed(2),
    }, "server");
  }

  // ── API Calls ─────────────────────────────────────────────

  async apiCall(path, body, uploadToken = null) {
    const token = await this.getAccessToken();

    // Acquiring the access token is a network round trip of its own; a cancel
    // landing during it must not be followed by a create-session or
    // complete-upload request.
    if (uploadToken?.cancelled) throw new UploadCancelledError();

    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API call failed (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  /**
   * Call complete-upload with retry (up to 3 times).
   * This is critical — if it fails after upload, the file sits in GCS unprocessed.
   */
  async finalizeUpload(audioFileUUID, fileSize, fileName, duration, fingerprint, token) {
    this.log("finalize.start", { audioFileUUID }, "server");

    for (let attempt = 0; attempt < this.FINALIZE_MAX_RETRIES; attempt++) {
      // Re-checked each attempt: a cancel can land while a completion request
      // is in flight, and retrying afterwards would finalize a cancelled file.
      if (token?.cancelled) throw new UploadCancelledError();
      try {
        await this.apiCall(
          "/upload/complete-upload",
          {
            audioFileUUID,
            fileSize,
            originalFileName: fileName,
            duration,
            language: this.language || undefined,
          },
          token
        );

        // The request may have succeeded, but if the attempt was cancelled
        // while it was in flight the caller must not go on to emit success.
        if (token?.cancelled) throw new UploadCancelledError();

        this.log("finalize.complete", { audioFileUUID }, "server");

        // Clear session from localStorage (session URI is an auth token)
        this.clearSession(fingerprint);
        return;
      } catch (err) {
        // Our own cancellation signal — not a finalize failure. Leaving it to
        // the retry path would sleep, log a false failure, and keep a timer
        // alive past teardown.
        if (err?.cancelled) throw err;

        this.log("finalize.failed", {
          audioFileUUID,
          error: err.message,
          attempt: attempt + 1,
        }, "server");

        if (attempt < this.FINALIZE_MAX_RETRIES - 1) {
          await this.sleep(this.calculateBackoff(attempt));
        } else {
          // All retries exhausted — keep session in localStorage for crash recovery
          this.updateSessionState(fingerprint, { status: "uploaded_pending_finalize" });
          throw err;
        }
      }
    }
  }

  // ── Main Upload Handler ───────────────────────────────────

  async handleUpload(fileIDs) {
    if (fileIDs.length === 0) return;

    const files = this.uppy.getFilesByIds(fileIDs).filter(Boolean);
    if (files.length === 0) return;

    // Tokens are created BEFORE upload-start is emitted: a listener that tears
    // the plugin down synchronously must be able to cancel this batch, and a
    // token that does not exist yet cannot be cancelled.
    const tokens = new Map();
    try {
      for (const f of files) tokens.set(f.id, this.beginUpload(f.id));
      // Emit batch-level upload-start for Uppy Dashboard progress bar
      this.uppy.emit("upload-start", files);
    } catch (err) {
      for (const token of tokens.values()) this.endUpload(token);
      throw err;
    }


    for (const file of files) {
      const token = tokens.get(file.id);
      try {
        const assertLive = () => {
          if (token.cancelled) throw new UploadCancelledError();
        };
        // A teardown triggered while an EARLIER file in this batch was
        // uploading must stop this one before it extracts a duration or opens
        // a GCS session that nothing will ever finish.
        assertLive();

        const fingerprint = this.generateFingerprint(file);

        // Step 1: Extract duration client-side
        let duration = null;
        try {
          duration = await extractDuration(file.data);
          this.log("duration.extracted", { duration });
        } catch (err) {
          this.log("duration.failed", { error: err.message });
        }

        // Step 2: Check for existing upload session
        let sessionUri, audioFileUUID, objectPath;
        let startOffset = 0;
        const existingSession = this.getStoredSession(fingerprint);

        if (existingSession) {
          // Validate session matches this file (prevent fingerprint collision corruption)
          const sizeMatch = existingSession.totalSize === (file.data?.size || file.size);
          const nameMatch = existingSession.fileName === file.name;
          if (!sizeMatch || !nameMatch) {
            this.log("session.mismatch", {
              fingerprint, storedName: existingSession.fileName,
              fileName: file.name, storedSize: existingSession.totalSize,
              fileSize: file.data?.size,
            }, "server");
            this.clearSession(fingerprint);
          }
        }

        if (existingSession && this.getStoredSession(fingerprint)) {
          this.log("session.check", { fingerprint, found: true, status: existingSession.status }, "server");

          // Case: upload completed but finalize failed — just finalize
          if (existingSession.status === "uploaded_pending_finalize") {
            this.log("finalize.recovered", {
              audioFileUUID: existingSession.audioFileUUID,
              timeSinceUpload: Date.now() - existingSession.createdAt,
            }, "server");

            assertLive();
            await this.finalizeUpload(
              existingSession.audioFileUUID,
              existingSession.totalSize,
              existingSession.fileName,
              duration || existingSession.duration,
              fingerprint,
              token
            );

            this.uppy.emit("upload-success", file, {
              status: 200,
              body: {
                audioFileUUID: existingSession.audioFileUUID,
                duration: duration || existingSession.duration,
                pipeline: "native",
                resumed: true,
              },
            });
            continue;
          }

          // Case: upload in progress — probe GCS for offset and resume
          sessionUri = existingSession.sessionUri;
          audioFileUUID = existingSession.audioFileUUID;
          objectPath = existingSession.objectPath;

          try {
            // Duration extraction above is an async round trip of its own.
            assertLive();
            startOffset = await this.probeUploadOffset(sessionUri, file.data.size);

            if (startOffset >= file.data.size) {
              // Upload already complete on GCS — just finalize
              this.log("session.resume", {
                fingerprint, resumeOffset: startOffset,
                sessionAge: Date.now() - existingSession.createdAt,
                alreadyComplete: true,
              }, "server");

              this.updateSessionState(fingerprint, { status: "uploaded_pending_finalize" });
              assertLive();
              await this.finalizeUpload(audioFileUUID, file.data.size, file.name, duration, fingerprint, token);

              this.uppy.emit("upload-success", file, {
                status: 200,
                body: { audioFileUUID, duration, pipeline: "native", resumed: true },
              });
              continue;
            }

            this.log("session.resume", {
              fingerprint, resumeOffset: startOffset,
              sessionAge: Date.now() - existingSession.createdAt,
            }, "server");
          } catch (err) {
            if (err.expired) {
              this.log("session.expired", {
                fingerprint,
                sessionAge: Date.now() - existingSession.createdAt,
              }, "server");
              this.clearSession(fingerprint);
              // Fall through to create new session
              sessionUri = null;
            } else {
              throw err;
            }
          }
        } else {
          this.log("session.check", { fingerprint, found: false }, "server");
        }

        // Step 3: Create new session if needed
        if (!sessionUri) {
          this.log("session.create", {
            fileName: file.name,
            fileSize: file.data.size,
            mimeType: file.type,
          }, "server");

          assertLive();
          const sessionResponse = await this.apiCall("/upload/create-session", {
            workspaceUUID: this.workspaceUUID,
            fileName: file.name,
            fileMimeType: file.type || "application/octet-stream",
            fileSize: file.data.size,
            language: this.language || undefined,
          }, token);

          if (sessionResponse.pipeline === "transloadit") {
            this.log("session.transloadit_fallback", {});
            continue;
          }

          sessionUri = sessionResponse.sessionUri;
          audioFileUUID = sessionResponse.audioFileUUID;
          objectPath = sessionResponse.objectPath;
          startOffset = 0;

          // Persist session for crash recovery
          this.storeSession(fingerprint, {
            sessionUri,
            audioFileUUID,
            objectPath,
            totalSize: file.data.size,
            createdAt: Date.now(),
            fileName: file.name,
            fileMimeType: file.type,
            lastKnownOffset: 0,
            status: "uploading",
            duration,
          });
        }

        // Step 4: Upload file in chunks
        try {
          await this.uploadFileChunked(file, sessionUri, startOffset, fingerprint, token);
        } catch (err) {
          if (err.expired) {
            // Session expired during upload — clear and fail
            this.clearSession(fingerprint);
          }
          throw err;
        }

        // Step 5: Mark as uploaded, finalize
        this.updateSessionState(fingerprint, { status: "uploaded_pending_finalize" });
        assertLive();
        await this.finalizeUpload(audioFileUUID, file.data.size, file.name, duration, fingerprint, token);

        // Step 6: Success
        this.uppy.emit("upload-success", file, {
          status: 200,
          body: {
            audioFileUUID,
            duration,
            pipeline: "native",
            resumed: startOffset > 0,
          },
        });
      } catch (err) {
        if (err?.cancelled) {
          this.log("upload.cancelled", { fileName: file.name }, "server");

          // If Uppy still holds the file, the cancel came from teardown rather
          // than from the user removing it — and returning quietly would let
          // Uppy mark an incomplete, error-free file as SUCCESSFUL and emit
          // `complete`. Only stay silent when the file is genuinely gone.
          if (this.uppy.getFile?.(file.id)) {
            this.uppy.emit(
              "upload-error",
              file,
              new Error("Upload cancelled")
            );
          }
          continue;
        }

        this.log("upload.failed", {
          error: err.message || String(err),
          fileName: file.name,
          fileSize: file.data?.size,
        }, "server");

        const errorMsg = err instanceof Error ? err.message : (err.message || err.body || `Upload failed (HTTP ${err.status || 'unknown'})`);
        this.uppy.emit("upload-error", file, err instanceof Error ? err : new Error(errorMsg));
      } finally {
        // The token's whole life is this attempt.
        this.endUpload(token);
      }
    }
  }
}
